import React, { useState, useRef, useEffect } from 'react';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import PropTypes from 'prop-types';
import LanguagePicker from './LanguagePicker';

// Assets
import RippleBackground from './ripple_bg.svg';
import SHARELogo from './SHARE-Logo-New.svg';
import SHAREIcon from './SHARE_icon.svg';
import IconSpeaker from 'cccisd-icons/volume-high';
import IconStop from 'cccisd-icons/volume-mute2';

const SHAREBlue = '#154988';

const IntroModal = ({ sessionInfo }) => {
    const [language, setLanguage] = useState('en');
    const [isSpeaking, setIsSpeaking] = useState(false);

    const { providerName, schoolName, studentName, isStarted, reporterType } = sessionInfo;
    const introModalRef = useRef(null);

    const ttsContent = {
        en: {
            title: 'Welcome to the share online survey system.',
            body: `${providerName} at ${schoolName} is asking you to provide information ${
                reporterType === `learner` ? '' : `about ${studentName}`
            }. You will be asked a series of questions about ${
                reporterType === `learner` ? `yourself` : studentName
            }. If you close out of the survey, your progress will be saved.`,
        },
        es: {
            title: 'Bienvenido al sistema de encuestas en línea share.',
            body: `${providerName} de ${schoolName} le solicita que proporcione información ${
                reporterType === `learner` ? '' : `sobre ${studentName}`
            }. Se le harán una serie de preguntas sobre ${
                reporterType === `learner` ? `usted mismo` : studentName
            }. Si cierra la encuesta, se guardará su progreso.`,
        },
    };

    useEffect(() => {
        if (isStarted === false) {
            if (introModalRef.current) {
                // Stop any ongoing speech synthesis before starting new speech
                stopSpeech();
                introModalRef.current.open();
            }
        }
    }, [isStarted, language]);

    const handleTTSClick = () => {
        if (isSpeaking) {
            stopSpeech();
            setIsSpeaking(false);
            return;
        }

        const utterance = new SpeechSynthesisUtterance(ttsContent[language].title + ' ' + ttsContent[language].body);
        utterance.lang = language === 'es' ? 'es-ES' : 'en-US';

        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        setIsSpeaking(true);
        speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
    };

    const getContent = () => {
        const modalContent = {
            title: (
                <div>
                    Welcome to the <img src={SHARELogo} alt="SHARE" style={{ height: '1.5em' }} /> online survey system.
                </div>
            ),
            body: (
                <div>
                    <h2 style={{ fontWeight: '300' }}>
                        {reporterType === `learner` && (
                            <>
                                Hi <strong>{studentName}</strong>,{' '}
                            </>
                        )}
                        <strong>{providerName}</strong> at <strong>{schoolName}</strong> is asking you to provide
                        information
                        {reporterType !== `learner` && (
                            <>
                                {' '}
                                about <strong>{studentName}</strong>
                            </>
                        )}
                        .
                    </h2>{' '}
                    <p
                        className="lead"
                        style={{ fontSize: 'x-large', fontWeight: '300', marginTop: '1em', color: '#333' }}
                    >
                        You will be asked a series of questions about{' '}
                        {reporterType === `learner` ? `yourself` : <>{studentName}</>}. If you close out of the survey,
                        your progress will be saved.
                    </p>
                </div>
            ),
        };

        if (language === 'es') {
            modalContent.title = (
                <div>
                    Bienvenido al sistema de encuestas en línea{' '}
                    <img src={SHARELogo} alt="SHARE" style={{ height: '1.5em' }} />.
                </div>
            );
            modalContent.body = (
                <div>
                    <h2 style={{ fontWeight: '300' }}>
                        {reporterType === `learner` && (
                            <>
                                Hola <strong>{studentName}</strong>,{' '}
                            </>
                        )}
                        <strong>{providerName}</strong> de <strong>{schoolName}</strong> le solicita que proporcione
                        información
                        {reporterType !== `learner` && (
                            <>
                                {' '}
                                sobre <strong>{studentName}</strong>
                            </>
                        )}
                        .
                    </h2>{' '}
                    <p
                        className="lead"
                        style={{ fontSize: 'x-large', fontWeight: '300', marginTop: '1em', color: '#333' }}
                    >
                        Se le harán una serie de preguntas sobre{' '}
                        {reporterType === `learner` ? `usted mismo` : <>{studentName}</>}. Si cierra la encuesta, se
                        guardará su progreso.
                    </p>
                </div>
            );
        }

        return modalContent;
    };

    return (
        <Modal
            ref={introModalRef}
            title={getContent().title}
            size="large"
            onTop
            bodyStyle={{
                background: `url(${RippleBackground}) no-repeat center, linear-gradient(to top right, #fff, ${SHAREBlue})`,
                backgroundSize: 'cover',
                color: SHAREBlue,
            }}
            headerStyle={{
                color: SHAREBlue,
            }}
            afterClose={stopSpeech}
            render={({ closeModal }) => {
                const handleCloseModal = () => {
                    stopSpeech();
                    closeModal();
                };
                return (
                    <div
                        style={{
                            background: 'rgba(255, 255, 255, 0.8)',
                            padding: '2em',
                            borderRadius: '8px',
                            margin: '1em',
                        }}
                    >
                        {getContent().body}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1em' }}>
                            <div style={{ display: 'flex', gap: '0.5em' }}>
                                <LanguagePicker language={language} setLanguage={setLanguage} />
                                <Tooltip
                                    title={
                                        /* eslint-disable-next-line no-nested-ternary */
                                        isSpeaking
                                            ? language === 'en'
                                                ? 'Stop Reading.'
                                                : 'Detener lectura.'
                                            : language === 'en'
                                            ? 'Read Aloud.'
                                            : 'Leer en voz alta.'
                                    }
                                >
                                    <button className="btn btn-primary btn-sm" type="button" onClick={handleTTSClick}>
                                        {isSpeaking ? <IconStop /> : <IconSpeaker />}
                                    </button>
                                </Tooltip>
                            </div>
                            <button className="btn btn-primary btn-lg" type="button" onClick={handleCloseModal}>
                                <div style={{ display: 'flex' }}>
                                    <img src={SHAREIcon} alt="SHARE icon" style={{ height: '1.5em' }} />{' '}
                                    <span>{language === 'en' ? `Start` : `Iniciar`}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                );
            }}
        />
    );
};

IntroModal.propTypes = {
    sessionInfo: PropTypes.object.isRequired,
};

export default IntroModal;
